import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Layout, Menu } from "antd";
import React, { Fragment } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink, withRouter } from "react-router-dom";
import {
  MainMenuItemInterface,
  SubMenuItemInterface,
} from "../helpers/shared-interfaces";
import { checkPermission } from "../helpers/utilities";
import "../styles/components/Sidebar.css";
import Logo from "./Logo";

const { SubMenu } = Menu;

export function MenuItemFactory(data: SubMenuItemInterface): JSX.Element {
  const { route, name, icon: AntdIcon } = data;
  return (
    <MenuItem key={route}>
      <NavLink exact to={route}>
        <AntdIcon style={{ marginRight: "8px" }} />
        <span>{name}</span>
      </NavLink>
    </MenuItem>
  );
}

function canAccessMenuItem(
  user: ReturnType<typeof JSON.parse>,
  entitlement: string | undefined,
): boolean {
  if (!entitlement) return true;
  return checkPermission(user, entitlement).canAccess;
}

function hasAccessibleDescendant(
  user: ReturnType<typeof JSON.parse>,
  subMenus: SubMenuItemInterface[],
): boolean {
  return subMenus.some((item) => {
    if (canAccessMenuItem(user, item.entitlement)) return true;
    if (item.subMenus) return hasAccessibleDescendant(user, item.subMenus);
    return false;
  });
}

export function SubMenuItemFactory(
  data: MainMenuItemInterface,
  user?: ReturnType<typeof JSON.parse>,
) {
  const { icon: AntdIcon, subMenus } = data;
  if (subMenus && subMenus.length > 0) {
    const visibleSubMenus = user
      ? subMenus.filter((item) => {
          if (item.subMenus) {
            return hasAccessibleDescendant(user, [item]);
          }
          return canAccessMenuItem(user, item.entitlement);
        })
      : subMenus;
    return (
      <SubMenu
        icon={<AntdIcon style={{ marginRight: "8px" }} />}
        title={data.name}
        key={data.key}
      >
        {visibleSubMenus.map((menuItem) => {
          const menuItemWithKey = { ...menuItem, key: menuItem.route };
          return SubMenuItemFactory(
            menuItemWithKey as MainMenuItemInterface,
            user,
          );
        })}
      </SubMenu>
    );
  } else {
    return MenuItemFactory(data);
  }
}

export function MenuFactory(
  data?: MainMenuItemInterface[],
): (JSX.Element | null)[] | null {
  if (!data) {
    return null;
  }

  const stringifiedUser = localStorage.getItem("user");
  const user = JSON.parse(stringifiedUser as string);

  return data.map(function (
    menuItem: MainMenuItemInterface,
  ): JSX.Element | null {
    if (menuItem.entitlement) {
      const { canAccess } = checkPermission(user, menuItem.entitlement);
      if (!canAccess) return null;
    } else if (menuItem.subMenus) {
      if (!hasAccessibleDescendant(user, menuItem.subMenus)) return null;
    } else {
      return null;
    }

    if (!menuItem.subMenus) {
      return MenuItemFactory(menuItem);
    } else {
      return SubMenuItemFactory(menuItem, user);
    }
  });
}

const { Sider } = Layout;
const { Item: MenuItem } = Menu;

interface State {
  current: string;
  hideAppName: boolean;
  collapsed: boolean;
}
interface Props extends RouteComponentProps {
  menuList?: MainMenuItemInterface[];
}

export class Sidebar extends React.Component<Readonly<Props>, State> {
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = { current: "", hideAppName: false, collapsed: false };
    this.toggleAppName = this.toggleAppName.bind(this);
  }

  static getDerivedStateFromProps(
    props: RouteComponentProps,
    state: State,
  ): State | null {
    if (props.location.pathname !== state.current) {
      return { ...state, current: props.location.pathname };
    }
    return null;
  }

  componentDidMount(): void {
    this.setState({ current: this.props.location.pathname });
  }

  toggleAppName = (collapsed: boolean): void => {
    this.setState({
      hideAppName: collapsed,
      collapsed,
    });
  };

  toggleCollapsed = (): void => {
    this.setState((prevState) => ({
      collapsed: !prevState.collapsed,
      hideAppName: !prevState.collapsed,
    }));
  };

  render(): JSX.Element | null {
    const { current, hideAppName, collapsed } = this.state;
    const { menuList } = this.props;
    const openSub = `/${current.split("/")[1]}`;
    const selectedMenu = `/${current.split("/")[1]}`;

    return (
      <Fragment>
        <Sider
          onCollapse={this.toggleAppName}
          collapsible
          trigger={null}
          collapsed={collapsed}
          breakpoint="md"
          width={280}
          theme="dark"
          className="app-sider"
          style={{
            height: "100vh",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "scroll",
                overflowX: "hidden",
              }}
            >
              <div
                style={{
                  textAlign: "left",
                  padding: "12px 20px 18px 20px",
                  boxSizing: "border-box",
                }}
              >
                <Logo
                  style={{
                    height: "33px",
                  }}
                />
                {!hideAppName ? (
                  <span
                    id="crm-name"
                    style={{
                      fontWeight: "bold",
                      fontSize: "20px",
                      position: "relative",
                      marginLeft: "5px",
                      top: "4px",
                      color: "white",
                    }}
                  >
                    PHOENIX
                  </span>
                ) : null}
              </div>
              <Menu
                style={{
                  marginTop: "50px",
                  paddingBottom: "24px",
                }}
                defaultOpenKeys={[openSub]}
                selectedKeys={[selectedMenu]}
                mode="inline"
                theme="dark"
              >
                {MenuFactory(menuList)}
              </Menu>
            </div>
            <div
              className="ant-layout-sider-trigger"
              onClick={this.toggleCollapsed}
              style={{
                width: "100%",
                flexShrink: 0,
                position: "relative",
                left: "auto",
                bottom: "auto",
              }}
            >
              <span
                role="img"
                aria-label={collapsed ? "right" : "left"}
                className={`anticon anticon-${collapsed ? "right" : "left"}`}
              >
                {collapsed ? <RightOutlined /> : <LeftOutlined />}
              </span>
            </div>
          </div>
        </Sider>
      </Fragment>
    );
  }
}

export default withRouter(Sidebar);
